import _ from 'lodash';
import { Commit, Dispatch } from 'vuex';
import { EditStoreBase } from '@/main/store/@types/edit.d';
import {
	DEFAULT,
	ENTITY,
	CREATE,
	UPDATE,
	DELETE,
	TABLE_SCHEMA,
	UPDATE_BASIC_TABLE_SCHEMA,
	TABLE_DATA,
	UPDATE_BASIC_TABLE_DATA,
	TABLE_SCHEMA_DATA,
	UPDATE_BASIC_TABLE_SCHEMA_DATA, TABLE_DATA_EXPORT, TABLE_DATA_DELETE, ERROR,
} from '@/main/store/listEditTypes';
import { writeAction, commitAction, updateEditActionNoLoading } from '@/main/store/actionsFunctions';
import { BaseApi, DependentTable } from '@/main/baseApi';
import { columnsToCamelCase, getColumnsRowMapObject } from '@/main/utils/table';
import { forceFileDownload, parseObj2Csv, renameKeys } from '@/main/utils/fileHelper';

export function editActions({ apiProvider, depTableNames = [] }: { apiProvider: () => BaseApi, depTableNames: readonly string[] }) {
	const actions = {
		/**
		 * Generates an action that commits UPDATE_BASIC_EDIT mutation.
		 * Calls {@link apiProvider.default} with an argument `filter`.
		 *
		 * Using data from `filter` and backend database, loads component with reasonable
		 * default values for an entity (to be possibly created later) whose partial
		 * data are known and are in `filter`.
		 *
		 * Uses {@link updateEditActionNoLoading} helper function.
		 *
		 * @returns this.
		 */
		[DEFAULT](
			{ state, commit, dispatch }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch },
			filter: object,
		) {
			return updateEditActionNoLoading(state, commit, dispatch, apiProvider().default(filter, { cancelTokenKey: DEFAULT, commit }));
		},

		/**
		 * Generates an action that commits UPDATE_BASIC_EDIT mutation.
		 * Calls {@link apiProvider.entity} with an argument `id`.
		 *
		 * Loads component data of an existing entity
		 * using the {@link updateEditActionNoLoading} helper function.
		 *
		 * @returns this.
		 */
		[ENTITY](
			{ state, commit, dispatch }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch },
			params: { id: string | number, edit: boolean | null },
		) {
			return updateEditActionNoLoading(state, commit, dispatch, apiProvider().entity(params.id, params.edit, { cancelTokenKey: ENTITY, commit }));
		},

		/**
		 * Generates an action that creates a request to create an entity on backend,
		 * if goBack then calls `router.back()`.
		 *
		 * Uses the {@link writeAction} helper function.
		 *
		 * @returns this.
		 */
		[CREATE](
			{ state, commit, dispatch }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch },
			{ goBack }: { goBack: boolean },
		) {
			return writeAction(
				state,
				commit,
				dispatch,
				apiProvider().createEntity(state.edit),
				goBack,
			);
		},

		/**
		 * Generates an action that creates a request to update an entity on backend,
		 * if goBack then calls `router.back()`.
		 *
		 * Uses the {@link writeAction} helper function.
		 *
		 * @returns this.
		 */
		[UPDATE](
			{ state, commit, dispatch }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch },
			{ id, goBack }: { id: string | number, goBack: boolean },
		) {
			return writeAction(
				state,
				commit,
				dispatch,
				apiProvider().updateEntity(id, state.edit),
				goBack,
			);
		},

		/**
		 * Generates an action that creates a request to delete an entity on backend,
		 * if goBack then calls `router.back()`.
		 *
		 * Uses the {@link writeAction} helper function.
		 *
		 * @returns this.
		 */
		[DELETE](
			{ state, commit, dispatch }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch },
			{ id, goBack }: { id: string | number, goBack: boolean },
		) {
			return writeAction(
				state,
				commit,
				dispatch,
				apiProvider().deleteEntity(id),
				goBack,
			);
		},
	};

	depTableNames.map(
		(name) => new DependentTable(name, apiProvider),
	).forEach((dependentTable) => {
		const schema = `${dependentTable.name}_${TABLE_SCHEMA}`;
		const data = `${dependentTable.name}_${TABLE_DATA}`;
		const schemaData = `${dependentTable.name}_${TABLE_SCHEMA_DATA}`;
		const exportData = `${dependentTable.name}_${TABLE_DATA_EXPORT}`;
		const deleteEntity = `${dependentTable.name}_${TABLE_DATA_DELETE}`;

		/* eslint-disable operator-linebreak */
		(actions as any)[schema] =
			({ commit, dispatch }: { commit: Commit, dispatch: Dispatch }) => commitAction(
				commit,
				dispatch,
				dependentTable.tableSchema({ cancelTokenKey: schema, commit }),
				dependentTable.name,
				UPDATE_BASIC_TABLE_SCHEMA,
				{ tableName: dependentTable.name },
			);

		(actions as any)[data] =
			(
				{ commit, dispatch, getters }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch, getters: any },
				payload: { filter?: {} },
			) => commitAction(
				commit,
				dispatch,
				dependentTable.tableData(payload?.filter ?? getters[`${dependentTable.name}Filter`], { cancelTokenKey: data, commit }),
				dependentTable.name,
				UPDATE_BASIC_TABLE_DATA,
				{ tableName: dependentTable.name },
			);

		(actions as any)[schemaData] =
			(
				{ commit, dispatch, getters }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch, getters: any },
				payload: { filter?: {} },
			) => commitAction(
				commit,
				dispatch,
				dependentTable.tableSchemaData(payload?.filter ?? getters[`${dependentTable.name}Filter`], { cancelTokenKey: data, commit }),
				dependentTable.name,
				UPDATE_BASIC_TABLE_SCHEMA_DATA,
				{ tableName: dependentTable.name },
			);

		(actions as any)[exportData] =
			(
				{ state, commit, dispatch, getters }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch, getters: any },
				payload: { filter?: {} },
			) => {
				const tableData = [] as any;
				state.dependentTables[dependentTable.name].rows.forEach((row: any) => {
					const rowData = getColumnsRowMapObject(columnsToCamelCase(state.dependentTables[dependentTable.name].columns), row.cells);
					tableData.push(rowData);
				});
				const formattedObj = tableData.map((row: any) => renameKeys(row));
				const url = `data:text/csv;charset=utf-8,%EF%BB%BF",${encodeURIComponent(parseObj2Csv(formattedObj))}`;
				return forceFileDownload(url);
			};

		(actions as any)[deleteEntity] =
			async (
				{ state, commit, dispatch, getters }: { state: EditStoreBase, commit: Commit, dispatch: Dispatch, getters: any },
				{ id }: { id: number },
			) => {
				try {
					const response = await dependentTable.tableDeleteEntity(id);
					if (response.status !== 200) throw Error(`delete response status not 200 !== ${response.status}`);
					commit('updateTableRow', { tableName: dependentTable.name, rowId: id });
				} catch (error) {
					dispatch(ERROR, { objectName: state.name, error }, { root: true });
				}
			};
	});

	return actions;
}
